import { faHotel } from '@fortawesome/pro-regular-svg-icons';
import type ItineraryOrder from '../ItineraryOrder';
import ItineraryPackage from '../ItineraryPackage';
import { type CardLabel, type PassengerCount } from '../types';
import { ItineraryVerifyException, VerificationStatus } from '../Exceptions';
import PriceClass from '../PriceClass';
import HotelBillHandler from './HotelBillHandler';
import { type HotelAmenity, type HotelCommentView } from './HotelPackageTypes';
import HotelPriceClass, { HotelExtraOptions } from './HotelPriceClass';
import HotelRoomType from './HotelRoomType';
import PassengerForm from '@itinerary/PassengerForm';
import VerifyResult from '@itinerary/VerifyResult';
import {
    HotelApiApiHotelCityNameHotelNameCalendarGet,
    HotelApiApiHotelCityNameHotelNameRoomsGet,
    type HotelCalendarDto,
    type HotelFaqListView,
    type HotelResponseHotel,
    type HotelSearchView,
    type HotelSpecialOffer,
    type PhotoSizeCollection,
    type PopularPlaceView,
    RoomReserveApiRoomReserveBookRoomsPost,
} from '@clients/HotelApi';
import { PassengerFields } from '@store/form';
import { type InvoiceEntry } from '@store/invoice';
import { ReserveType } from '@utils/itinerary';
import { PaxType } from '@utils/types';
import { ServiceDiscountTypes } from '@clients/AuthApi';
import { amenityIconMap } from '@static/hotel-amenities';

export default class HotelPackage extends ItineraryPackage<HotelPriceClass> {
    hotel: HotelResponseHotel | HotelSearchView | null = null; // just for repair prototype
    override reserveType = ReserveType.Hotel;
    id: number | null = null;
    title = '';
    engTitle = '';
    photos: PhotoSizeCollection[] = [];
    isMrBilitOffer = false;
    description = '';
    preTotalPrice = 0;
    prePayable = 0;
    rating = 0;
    saleCount = 0;
    accommodationId = 0;
    serviceLevelId = 0;
    amenities: HotelAmenity[] = [];
    address = '';
    hotelStar = 0;
    mapImage = '';
    popularPlaces: PopularPlaceView[] = [];
    message: string | null = null;
    loadedPrices: HotelRoomType[] = [];
    override termsAndConditions = '';
    comments: HotelCommentView[] = [];
    faqs: HotelFaqListView[] = [];
    requestFromHotel = '';
    commentsLength: number | undefined = undefined;
    cityTitle = '';
    latitude? = 0;
    longitude? = 0;
    hotelCapacity = 1;
    checkinHour = '';
    checkoutHour = '';
    providerName = '';
    hasFreeTransfer = false;
    isLoadingRooms: boolean | null = null;
    isStatic = false;
    override specialOffers: HotelSpecialOffer[] = [];
    cityId: number | null = null;
    fromDate?: string;
    toDate?: string;
    adultCount: number;
    childCount: number;
    infantCount: number;
    roomCount: number;
    cityName: string;
    persianCityName: string;
    hotelName: string;
    closed: boolean;
    HotelSchedule: HotelCalendarDto[] = [];
    constructor(params?: {
        hotel?: HotelResponseHotel | HotelSearchView | null;
        fromDate?: string;
        toDate?: string;
        adultCount?: number;
        childCount?: number;
        infantCount?: number;
        roomCount?: number;
        cityName?: string;
        hotelName?: string;
        totalPrice?: number;
        payable?: number;
        persianCityName?: string;
        isStatic?: boolean;
    }) {
        super();
        const {
            cityName = '',
            persianCityName = '',
            hotelName = '',
            hotel = null,
            fromDate,
            toDate,
            payable,
            adultCount = 1,
            childCount = 0,
            infantCount = 0,
            roomCount = 1,
            totalPrice,
        } = params || {};
        this.closed = !!hotel?.closed;
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.adultCount = adultCount;
        this.childCount = childCount;
        this.infantCount = infantCount;
        this.roomCount = roomCount;
        this.cityName = cityName;
        this.hotelName = hotelName;
        this.persianCityName = persianCityName;
        this.isStatic = !!params?.isStatic;
        if (!hotel) {
            return;
        }
        this.concreteType = ReserveType.Hotel;
        this.passengerCountEditTypes = [];
        this.hasFreeTransfer = !!hotel.hasFreeTransfer;
        this.specialOffers =
            hotel.hotelSpecialOffers
                ?.filter(
                    label =>
                        !label.startTrip ||
                        dayConfig
                            .gregory(label.startTrip)
                            .isBefore(this.fromDate)
                )
                .filter(
                    label =>
                        !label.endTrip ||
                        dayConfig.gregory(label.endTrip).isAfter(this.toDate)
                )
                .slice()
                .sort(
                    (label1, label2) =>
                        (label1.priority || 0) - (label2.priority || 0)
                ) || [];
        if ((hotel as HotelSearchView).englishName !== undefined) {
            const hotel_ = hotel as HotelSearchView;
            this.commentsLength = hotel_?.numberOfComments;
            this.providerName = hotel_?.providerName || '';
            this.hotel = hotel_;
            this.id = hotel_.id || null;
            this.uniqueId = hotel_.id?.toString() || '';
            this.title = hotel_.name || '';
            this.engTitle = hotel_.englishName || '';
            this.cityName = hotel_.englishCityName || '';
            this.persianCityName = hotel_.cityName || '';
            this.hotelCapacity = hotel_.minRoomPriceCapacity || 1;
            if (hotel_.bannerPhotos) {
                this.photos = hotel_.bannerPhotos;
            } else if (hotel_.photos) {
                this.photos = [hotel_.photos];
            }
            this.preTotalPrice = totalPrice || hotel_.totalPrice || 0;
            this.prePayable = payable || hotel_.payable || 0;
            this.rating = hotel_.rating || 0;
            this.saleCount = hotel_.saleCount || 0;
            this.accommodationId = hotel_.hotelTypeId || 0;
            this.serviceLevelId = hotel_.serviceLevel?.id || 0;
            this.amenities = (hotel_.amenities || [])
                .filter(a => !!a.iconHexCode)
                .map(am => ({
                    id: am.id || '',
                    title: am.title || '',
                    icon: amenityIconMap[am.iconHexCode!] || faHotel,
                }));
            this.address = hotel_.shortAddress || '';
            this.hotelStar = hotel_.hotelTypeStars || 0;
            this.isMrBilitOffer = hotel_.isMrBilitOffer || false;
            this.longitude = hotel_.longitude;
            this.latitude = hotel_.latitude;
        } else {
            const hotel_ = hotel as HotelResponseHotel;
            this.commentsLength = hotel_?.comments?.length;
            this.hotel = hotel_;
            this.title = [hotel_.hotelTypeName || '', hotel_.title || '']
                .filter(Boolean)
                .join(' ');
            this.id = hotel_.id || null;
            this.hotelStar = hotel_.stars || 0;
            this.mapImage = hotel_.locationImage || '';
            this.rating = hotel_.rating || 0;
            this.address = hotel_.address || '';
            this.amenities = (hotel_.amenities || [])
                .filter(a => !!a.iconHexCode)
                .map(am => ({
                    id: am.title || '',
                    title: am.title || '',
                    icon: amenityIconMap[am.iconHexCode!] || faHotel,
                }));
            this.checkinHour = hotel_.checkinHour || '';
            this.checkoutHour = hotel_.checkoutHour || '';
            this.photos = hotel_.allPhotos || [];
            this.description = hotel_.description || '';
            this.popularPlaces = (hotel_.popularPlaces || []).map(p => ({
                ...p,
                distance: Math.round((p.distance || 0) / 100) / 10,
            }));
            this.message = hotel_.message || null;
            this.termsAndConditions = hotel_.termsAndConditions || '';
            this.comments = (hotel_.comments || []).map(c => ({
                ...c,
                rating:
                    c.rating !== null && c.rating !== undefined
                        ? Math.round(c.rating * 2 * 10) / 10
                        : null,
            }));
            this.faqs = hotel_.hotelFaqs || [];
            this.cityTitle = hotel_.cityName || '';
            this.cityId = hotel_.cityId || null;
            this.latitude = hotel_.latitude || 0;
            this.longitude = hotel_.longitude || 0;
        }
    }

    override get prices(): HotelRoomType[] {
        return this.loadedPrices;
    }

    get floatingMode() {
        const { featureFlags } = storeToRefs(usePostHog());

        if (featureFlags.value?.['show-floating-all']) {
            return !!this.selectedPrices?.some(price => price.isFloating);
        } else {
            return this.prices?.every(price => price.isFloating);
        }
    }

    async loadRooms() {
        try {
            this.isLoadingRooms = true;
            const { data } = await HotelApiApiHotelCityNameHotelNameRoomsGet({
                cityName: this.cityName,
                hotelName: this.hotelName,
                from: dayConfig.gregory(this.fromDate).format(formatter()),
                to: dayConfig.gregory(this.toDate).format(formatter()),
                adultCount: this.adultCount,
                childrenCount: this.childCount,
                roomCount: this.roomCount,
            });
            const rooms = data.rooms || [];
            this.loadedPrices = rooms.flatMap(
                r =>
                    r.prices?.map(
                        price =>
                            new HotelRoomType(
                                {
                                    ...r,
                                    checkinHour: this.checkinHour,
                                    checkoutHour: this.checkinHour,
                                },
                                price
                            )
                    ) || []
            );
        } catch (e) {
            reportException(e);
        } finally {
            this.isLoadingRooms = false;
        }
    }

    override get isSpecialOffer(): boolean {
        return this.specialOffers.some(
            offer => offer.showPriorityImpactOnSorting
        );
    }

    get location(): { latitude: number; longitude: number } {
        return { latitude: this.latitude || 0, longitude: this.longitude || 0 };
    }

    get serverLabels() {
        return (
            this.specialOffers?.map(label => ({
                title: label.title || '',
                backgroundColor: label.badgeColorOnCard || '',
                // icons cannot be fetched from server
                // iconName: label.icon || '',
            })) || []
        );
    }

    override get hasPassengerDelete() {
        return false;
    }

    override get hasPassengerDuplicate() {
        return true;
    }

    override get labels(): CardLabel[] {
        const labels: CardLabel[] = [];
        if (this.hasFreeTransfer) {
            labels.push({
                title: 'ترانسفر رایگان',
                color: 'var(--yellow-700)',
                backgroundColor: 'var(--yellow-100)',
            });
        }
        if (this.isMrBilitOffer) {
            labels.push({
                title: 'پیشنهاد ویژه',
                color: 'var(--red-600)',
                backgroundColor: 'var(--red-100)',
            });
        }
        if (this.providerName) {
            labels.push({
                title: this.providerName,
            });
        }
        labels.push(...this.serverLabels);
        return labels;
    }

    override get invoiceTitle() {
        return this.title;
    }

    override get selectedPricesWithPriceClass():
        | [HotelPriceClass, HotelRoomType][]
        | null {
        return (
            this.selectedPrices?.map(price => {
                const priceType = this.prices.find(pt =>
                    pt.classes.some(p => p.id === price.id)
                ) as HotelRoomType;
                return [price, priceType];
            }) || null
        );
    }

    get totalPrice() {
        return useAuthStore().customDiscounts?.Hotel
            ? this.prePayable
            : this.preTotalPrice;
    }

    get payable() {
        return PriceClass.calculatePreDiscountPrice(
            this.prePayable,
            ServiceDiscountTypes.Hotel
        );
    }

    get photo(): PhotoSizeCollection {
        return this.photos[0] || {};
    }

    get nights(): number {
        if (this.toDate && this.fromDate) {
            return dayConfig.gregory(this.toDate).diff(this.fromDate, 'day');
        }
        return 1;
    }

    get percentStar(): number {
        return (this.hotelStar || 0) * 20;
    }

    override get raiseTitle(): string {
        return `گزینه های هتل ${this.title}`;
    }

    async loadHotelSchedule() {
        if (!this.cityName || !this.hotelName) {
            return;
        }
        try {
            const { data } = await HotelApiApiHotelCityNameHotelNameCalendarGet(
                {
                    cityName: this.cityName,
                    hotelName: this.hotelName,
                }
            );

            this.HotelSchedule = data;
        } catch (e) {
            handleException(e);
        }
    }

    override async createBill(
        _count: PassengerCount,
        _billId: number | null
    ): Promise<VerifyResult> {
        const defaultFields =
            PassengerFields.Gender | PassengerFields.Nationality;
        try {
            const { data: roomData } =
                await RoomReserveApiRoomReserveBookRoomsPost({
                    bookRoomRequest: {
                        isFloatingReserve: this.floatingMode,
                        adultCount: this.adultCount,
                        childrenCount: this.childCount,
                        infantCount: this.infantCount,
                        requestFromHotel: this.requestFromHotel,
                        rooms: this.selectedPrices!.map(price => ({
                            id: price.id,
                            hasEarlyCheckin: price.selectedExtraOptions.some(
                                extra =>
                                    extra.id === HotelExtraOptions.EarlyCheckin
                            ),
                            hasLateCheckout: price.selectedExtraOptions.some(
                                extra =>
                                    extra.id === HotelExtraOptions.LateCheckout
                            ),
                            extraPersons: price.selectedExtraPerson.map(age =>
                                dayConfig
                                    .gregory()
                                    .add(-Number(age), 'year')
                                    .toISOString()
                            ),
                        })),
                    },
                });

            const data = {
                ...roomData,
                billDetails:
                    roomData.billDetails?.map(({ payablePrice, ...rest }) => ({
                        ...rest,
                        payablePrice: PriceClass.calculatePreDiscountPrice(
                            payablePrice ?? 0,
                            ServiceDiscountTypes.Hotel
                        ),
                    })) ?? [],
            };

            const foreignerExtraFields =
                PassengerFields.FirstName |
                PassengerFields.LastName |
                PassengerFields.Nationality |
                PassengerFields.NationalityCode |
                PassengerFields.PassportNumber |
                PassengerFields.PassportExpiry;
            const iranianExtraFields =
                PassengerFields.PersianFirstName |
                PassengerFields.PersianLastName |
                PassengerFields.NationalCode;
            return Promise.resolve(
                new VerifyResult(new HotelBillHandler(), {
                    billCode: data.billCode!,
                    mac: data.mac || '',
                    billId: data.billId,
                    reserveId: data.reserveId,
                    remainingTime: 10,
                    payablePrice: (data.billDetails || []).reduce(
                        (payable, detail) =>
                            payable + (detail.payablePrice || 0),
                        0
                    ),
                    totalPrice: (data.billDetails || []).reduce(
                        (total, detail) => total + (detail.totalPrice || 0),
                        0
                    ),
                    discount: (data.billDetails || []).reduce(
                        (discount, detail) =>
                            discount +
                            ((detail.totalPrice || 0) -
                                (detail.payablePrice || 0)),
                        0
                    ),
                    priceEntries: [
                        {
                            prices: (data.billDetails || []).map(detail => ({
                                paxType: PaxType.Room,
                                count: detail.unitCount || 0,
                                price:
                                    (detail.totalPrice || 0) /
                                    (detail.unitCount || 1),
                                title: detail.title || undefined,
                                discount:
                                    ((detail.totalPrice || 0) -
                                        (detail.payablePrice || 0)) /
                                    (detail.unitCount || 1),
                            })),
                        },
                    ],
                    paxList: this.selectedPricesWithPriceClass?.map(
                        (price, i) =>
                            new PassengerForm({
                                type: PaxType.Adult,
                                title: `سرپرست اتاق ${i + 1}`,
                                defaultFields,
                                iranianExtraFields,
                                foreignerExtraFields,
                                buyExtraFields: price[0].extraBuyFields,
                                subTitle: price[1].title,
                            })
                    ),
                    isFloatingReserve:
                        data.isFloatingReserve || this.floatingMode,
                    reserveNotes: [
                        ...(this.floatingMode
                            ? [
                                  `مسافر محترم، شما متقاضی رزرو اتاقی هستید که ظرفیت و قیمت آن
                               نیازمند استعلام اپراتورهای ما از هتل مورد نظر است، در صورت
                               تمایل به رزرو این اتاق فرم زیر را تکمیل کرده و برای
                               بررسی‌های بیشتر ما منتظر بمانید.`,
                              ]
                            : []),
                        'برای هر اتاق فقط وارد کردن اطلاعات سرپرست کافی است و نیازی به ثبت اطلاعات سایر همراهان نیست.',
                    ],
                })
            );
        } catch (error) {
            throw new ItineraryVerifyException(
                VerificationStatus.UnknownError,
                (error as any).response?.message
            );
        }
    }

    get roomsEntryList(): InvoiceEntry[] {
        let entryList: InvoiceEntry[] = [];
        if (this.selectedPrices) {
            const countedPrices: HotelPriceClass[] = [];
            this.selectedPrices.forEach(price => {
                if (!countedPrices.some(p => p.id === price.id)) {
                    countedPrices.push(price);
                }
            });
            entryList = countedPrices.map(price => {
                const count: number =
                    this.selectedPrices!.reduce((count, pr) => {
                        if (pr.id === price.id) {
                            return count + 1;
                        }
                        return count;
                    }, 0) || 1;
                const singlePayable = PriceClass.calculatePreDiscountPrice(
                    price.singlePayable,
                    ServiceDiscountTypes.Hotel
                );
                return {
                    title: this.prices.find(pType =>
                        pType.classes.some(p => p.id === price.id)
                    )!.title,
                    count,
                    // why not using discount amount it self :/
                    discount: price.singlePrice - singlePayable,
                    paxType: PaxType.Room,
                    price: singlePayable,
                };
            });
        }
        return entryList;
    }

    get roomsEntryListWithFormExtraOptions(): InvoiceEntry[] {
        const roomEntryWithPriceId =
            this.selectedPrices?.reduce(
                (prev, current) => {
                    const existingEntry = prev.find(
                        entry => entry.id === current.id
                    );
                    if (existingEntry) {
                        existingEntry.count++;
                        return prev;
                    } else {
                        return [
                            ...prev,
                            {
                                title: this.prices.find(pType =>
                                    pType.classes.some(p => p.id === current.id)
                                )!.title,
                                count: 1,
                                discount:
                                    // why
                                    current.singlePrice - current.singlePayable,
                                paxType: PaxType.Room,
                                price: current.singlePayable,
                                id: current.id!,
                                showAsUnknown:
                                    !current.singlePayable && this.floatingMode,
                            },
                        ];
                    }
                },
                [] as (InvoiceEntry & { id: string })[]
            ) || [];
        const extraOptionWithPriceId =
            this.selectedPrices?.reduce(
                (prev, current) => {
                    const newEntries: (InvoiceEntry & {
                        priceId: string;
                        optionId: string;
                    })[] = [];
                    current.selectedExtraFormOptions.forEach(it => {
                        const prevEntry = prev.find(
                            entry =>
                                entry.priceId === current.id &&
                                entry.optionId === it.id
                        );

                        if (prevEntry) {
                            prevEntry.count++;
                        } else {
                            newEntries.push({
                                title: it.title,
                                price: it.price,
                                count: 1,
                                discount: 0,
                                paxType: PaxType.Room,
                                priceId: current.id!,
                                optionId: it.id,
                            });
                        }
                    });
                    return [...prev, ...newEntries];
                },
                [] as (InvoiceEntry & { priceId: string; optionId: string })[]
            ) || [];
        return roomEntryWithPriceId.reduce((prev, current) => {
            const extraOptions = extraOptionWithPriceId.filter(
                option => option.priceId === current.id
            );
            return [...prev, current, ...extraOptions];
        }, [] as InvoiceEntry[]);
    }

    get uniqueSelectedPrices(): HotelPriceClass[] {
        const agg = this.selectedPrices?.reduce((all, current) => {
            if (all.some(c => c.id === current.id)) {
                return all;
            } else {
                return [...all, current];
            }
        }, [] as HotelPriceClass[]);
        return agg || [];
    }

    get extraPersonEntryList(): InvoiceEntry[] {
        const entryList: InvoiceEntry[] = [];
        this.uniqueSelectedPrices.forEach(price => {
            const all =
                this.selectedPrices?.filter(p => p.id === price.id) || [];
            const count = all.reduce(
                (count, current) => count + current.selectedExtraPerson.length,
                0
            );
            if (count > 0) {
                entryList.push({
                    title: 'انتخاب تخت اضافه',
                    count,
                    discount: 0,
                    paxType: PaxType.Room,
                    price: price.extraBedPrice,
                    showAsUnknown: !price.extraBedPrice && this.floatingMode,
                });
            }
        });
        return entryList;
    }

    override loadPreInvoice(_count: {
        adultCount: number;
        childCount: number;
        infantCount: number;
    }): Promise<InvoiceEntry[]> {
        const extraOptions: InvoiceEntry[] = [];
        if (this.selectedPrices) {
            this.selectedPrices.forEach(price => {
                if (price.selectedExtraOptions?.length) {
                    price.selectedExtraOptions.forEach(extraOption => {
                        const prePriceDiscount =
                            PriceClass.calculatePreDiscountPrice(
                                extraOption.price,
                                ServiceDiscountTypes.Hotel
                            );
                        extraOptions.push({
                            title: extraOption.title,
                            price: prePriceDiscount,
                            count: 1,
                            discount: extraOption.price - prePriceDiscount,
                            paxType: PaxType.Room,
                            showAsUnknown:
                                !price.extraBedPrice && this.floatingMode,
                        });
                    });
                }
            });
        }
        return Promise.resolve([
            ...this.roomsEntryListWithFormExtraOptions,
            ...this.extraPersonEntryList,
            ...extraOptions,
        ]);
    }

    override get totalPayableForCheck(): number | null {
        if (this.selectedPrices) {
            return (
                this.selectedPrices.reduce(
                    (total, price) => total + price.totalPayableForCheck,
                    0
                ) || null
            );
        } else {
            return null;
        }
    }

    override get billMessage() {
        if (this.floatingMode) {
            return [
                {
                    message:
                        'قیمت و ظرفیت هتل مورد نظر شما در این مرحله قطعی نیست و نیازمند استعلام کارشناسان ما است.',
                },
            ];
        }
        return [];
    }

    override get totalPriceForCheck(): number | null {
        if (this.selectedPrices) {
            return (
                this.selectedPrices.reduce(
                    (total, price) => total + price.totalPriceForCheck,
                    0
                ) || null
            );
        } else {
            return null;
        }
    }

    override get totalCount(): number {
        return this.selectedPrices?.length || 0;
    }

    override get points(): number | null {
        return this.rating;
    }

    override get order(): ItineraryOrder {
        throw new Error('The method is not implemented.');
    }

    override get buyTooltip(): string {
        throw new Error('The method is not implemented.');
    }

    override get reservable(): boolean {
        return true;
    }

    override get key(): string {
        return `${this.id}`;
    }

    get cheapestPriceClass(): HotelPriceClass | null {
        const firstAvailableIndex = this.prices.findIndex(
            p => !!p.cheapestPriceClass
        );
        if (firstAvailableIndex < 0) {
            return null;
        }
        return this.prices.reduce((minPrice, price) => {
            if (
                price.cheapestPriceClass &&
                price.cheapestPriceClass.singlePayable < minPrice.singlePayable
            ) {
                return price.cheapestPriceClass;
            } else {
                return minPrice;
            }
        }, this.prices[firstAvailableIndex].cheapestPriceClass!);
    }

    /**
     * minimum single payable of prices
     */
    override get singlePayable(): number {
        if (this.cheapestPriceClass) {
            return this.cheapestPriceClass.singlePayable;
        } else {
            return 0;
        }
    }

    /**
     * minimum single price of prices
     */
    override get singlePrice(): number {
        if (this.cheapestPriceClass) {
            return this.cheapestPriceClass.singlePrice;
        } else {
            return 0;
        }
    }

    override checkNeedsRaiseClass(): boolean {
        return false;
    }

    override get callToReserve(): boolean {
        throw new Error('The method is not implemented.');
    }

    override haveSmEvent = true;

    static revive(data: HotelPackage) {
        const newPack = new HotelPackage();
        Object.assign(newPack, data);
        if (data.selectedPrices?.length) {
            newPack.selectedPrices = [];
            data.selectedPrices.forEach(price => {
                const priceClass = new HotelPriceClass(
                    price.room,
                    price.options
                );
                Object.assign(priceClass, price);
                newPack.selectedPrices!.push(priceClass);
            });
        }
        return newPack;
    }
}
